import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryFetch } from '@services/api';
import { queryKeys } from '@utils/consts';
import { MaterialReq } from './types';
import { DefaultApiResp, Material } from '@root/globalTypes';
import { useJobRefetch } from '@hooks/job/refetch';
import { useShowErrorToast } from '@utils/formHelpers';
import { useAccess } from '@hooks/access';

export const useUpdateMaterial = ({
  material,
  callback,
}: {
  material?: Material;
  callback?: (data: DefaultApiResp) => void;
}) => {
  const { refetch, stages } = useJobRefetch();
  const { showErrorToast } = useShowErrorToast();

  const queryClient = useQueryClient();
  const access = useAccess();
  const { mutate, isPending } = useMutation({
    mutationFn: ({ id, jobId, ...data }: MaterialReq): Promise<DefaultApiResp> =>
      queryFetch({
        url: `jobs/${jobId}/materials/${id}`,
        data: {
          material: data,
        },
        opt: { method: 'PUT' },
      }),
    onMutate: async ({ jobId, optionsAttributes }) => {
      if (!material) return;
      return stages.update({
        jobId,
        subtask: {
          id: material?.subtaskId || '',
          completed: true,
          materials: [
            {
              ...material,
              options: material?.options?.map((el, i) => ({
                ...el,
                finalQuantity: optionsAttributes?.[i].finalQuantity?.toString(),
              })),
            },
          ],
        },
      });
    },
    onError: (err, { jobId }, prev) => {
      console.error(err);
      if (access.fieldUser) return;
      stages.restore(jobId, prev);
    },
    onSuccess: async ({ success }) => {
      if (!success) return;
      if (access.fieldUser)
        await queryClient.refetchQueries({ queryKey: queryKeys.foremanMaterials });
      else await refetch();
    },
    onSettled: (data) => {
      if (!data) return;
      const { errors } = data as DefaultApiResp;
      if (data && callback) callback(data);
      if (errors) showErrorToast(errors);
    },
  });

  return {
    mutate,
    isLoading: isPending,
  };
};
